import { useState } from "react";
import { Scissors, Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELDS = [
  { key: "bust", label: "Bust", placeholder: "e.g. 36" },
  { key: "waist", label: "Waist", placeholder: "e.g. 30" },
  { key: "hips", label: "Hips", placeholder: "e.g. 39" },
  { key: "length", label: "Outfit length", placeholder: "e.g. 42" },
  { key: "sleeve", label: "Sleeve length", placeholder: "e.g. 18" },
  { key: "shoulder", label: "Shoulder", placeholder: "e.g. 15" },
];

/**
 * Flat-$15 alteration: customer fills their measurements in inches (all
 * optional — sometimes it's just "take in the waist"). Composed into a note
 * string that rides on the cart line into the Shopify order note.
 */
export const AlterationFeeModal = ({ open, onClose, onConfirm, productTitle }) => {
  const [vals, setVals] = useState({});
  const [extra, setExtra] = useState("");

  const set = (k) => (e) => setVals((v) => ({ ...v, [k]: e.target.value }));

  const submit = () => {
    const parts = FIELDS.filter((f) => (vals[f.key] || "").trim()).map((f) => `${f.label}: ${vals[f.key].trim()}`);
    if (extra.trim()) parts.push(`Notes: ${extra.trim()}`);
    const note = parts.length ? `${parts.join(", ")} (inches)` : "";
    setVals({});
    setExtra("");
    onConfirm(note);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none border-[#1A1A1A]" data-testid="alteration-fee-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Scissors size={18} className="text-[#B84A3B]" /> Alteration · $15 flat
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6E6E69] leading-relaxed">
          {productTitle ? <>For <b className="text-[#1A1A1A]">{productTitle}</b> — </> : null}
          tell us your measurements and our in-house tailor will adjust the piece before it ships.
          All fields in inches, all optional.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="overline text-[#6E6E69] flex items-center gap-1"><Ruler size={11} /> {f.label}</span>
              <input
                data-testid={`alteration-field-${f.key}`}
                value={vals[f.key] || ""}
                onChange={set(f.key)}
                placeholder={f.placeholder}
                inputMode="decimal"
                className="mt-1 w-full border border-[#E5E5DF] bg-[#FAF9F6] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#B84A3B]"
              />
            </label>
          ))}
        </div>
        <label className="block mt-3">
          <span className="overline text-[#6E6E69]">Anything else</span>
          <textarea
            data-testid="alteration-field-notes"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={2}
            placeholder="e.g. take in the waist slightly, keep the hem border"
            className="mt-1 w-full border border-[#E5E5DF] bg-[#FAF9F6] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#B84A3B] resize-none"
          />
        </label>
        <div className="flex gap-3 mt-5">
          <button
            data-testid="alteration-confirm-button"
            onClick={submit}
            className="flex-1 bg-[#1A1A1A] text-[#FFFFFF] py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors duration-300"
          >
            Add to bag · $15
          </button>
          <button
            data-testid="alteration-cancel-button"
            onClick={onClose}
            className="px-5 border border-[#E5E5DF] text-[0.72rem] font-mono tracking-[0.18em] uppercase text-[#6E6E69] hover:border-[#1A1A1A] transition-colors duration-300"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
